export type TeamMember = {
  name?: string | null;
  slug?: string | null;
  role?: string | null;
  description?: string | null;
  image?: string | null;
  isResearcher?: boolean | null;
  socials?: { platform?: string | null; url?: string | null }[] | null;
};

export type FlattenedTeamMember = TeamMember & {
  section: "heads" | "departments";
  department?: string;
  deptIndex?: number;
};

export type AboutTeamData = {
  heads?: { teamMember?: TeamMember[] | null } | null;
  departments?: {
    departments?: { title?: string | null; teamMember?: TeamMember[] | null }[] | null;
  } | null;
};

export function teamMemberSlug(name?: string | null): string {
  return (
    name
      ?.toLowerCase()
      .replace(/ /g, "-")
      .replace(/[\s?]+/g, "-")
      .replace(/[^\w-]+/g, "")
      .replace(/-+$/, "") || ""
  );
}

export function getMemberSlug(member?: Pick<TeamMember, "slug" | "name"> | null): string {
  const explicit = member?.slug?.trim();
  if (explicit) return explicit;
  return teamMemberSlug(member?.name);
}

export function flattenAboutTeamMembers(about?: AboutTeamData | null): FlattenedTeamMember[] {
  const members: FlattenedTeamMember[] = [];

  if (about?.heads?.teamMember) {
    members.push(
      ...about.heads.teamMember.map((member) => ({
        ...member,
        section: "heads" as const,
      }))
    );
  }

  if (about?.departments?.departments) {
    about.departments.departments.forEach((dept, deptIndex) => {
      if (dept?.teamMember) {
        members.push(
          ...dept.teamMember.map((member) => ({
            ...member,
            section: "departments" as const,
            department: dept.title ?? undefined,
            deptIndex,
          }))
        );
      }
    });
  }

  return members;
}

export function buildTeamMemberLookup(
  about?: AboutTeamData | null
): Map<string, FlattenedTeamMember> {
  const lookup = new Map<string, FlattenedTeamMember>();

  for (const member of flattenAboutTeamMembers(about)) {
    const slug = getMemberSlug(member);
    if (!slug || lookup.has(slug)) continue;
    lookup.set(slug, member);
  }

  return lookup;
}

export function findTeamMemberBySlug(
  about?: AboutTeamData | null,
  slug?: string | null
): FlattenedTeamMember | undefined {
  const normalized = slug?.trim();
  if (!normalized) return undefined;
  return buildTeamMemberLookup(about).get(normalized);
}

/** Original Tina node from about data (not a spread copy) for inline editing. */
export function findTeamMemberInAbout(
  about?: AboutTeamData | null,
  slug?: string | null
): { member: TeamMember; tinaPath: string } | undefined {
  const normalized = slug?.trim();
  if (!normalized || !about) return undefined;

  if (about.heads?.teamMember) {
    for (let index = 0; index < about.heads.teamMember.length; index++) {
      const member = about.heads.teamMember[index];
      if (member && getMemberSlug(member) === normalized) {
        return { member, tinaPath: `heads.teamMember.${index}` };
      }
    }
  }

  if (about.departments?.departments) {
    for (let deptIndex = 0; deptIndex < about.departments.departments.length; deptIndex++) {
      const department = about.departments.departments[deptIndex];
      if (!department?.teamMember) continue;

      for (let index = 0; index < department.teamMember.length; index++) {
        const member = department.teamMember[index];
        if (member && getMemberSlug(member) === normalized) {
          return {
            member,
            tinaPath: `departments.departments.${deptIndex}.teamMember.${index}`,
          };
        }
      }
    }
  }

  return undefined;
}

export function getAllTeamMemberSlugs(about?: AboutTeamData | null): string[] {
  return [...buildTeamMemberLookup(about).keys()];
}

export function getResearchersFromAbout(about?: AboutTeamData | null): TeamMember[] {
  const seen = new Set<string>();
  const researchers: TeamMember[] = [];

  for (const member of flattenAboutTeamMembers(about)) {
    if (!member.isResearcher) continue;

    const slug = getMemberSlug(member);
    if (!slug || seen.has(slug)) continue;

    seen.add(slug);
    researchers.push(member);
  }

  return researchers;
}
